import React from 'react';

/**
 * NoFee brand logo.
 *
 * The MARK (the oval outline + cloche + hand cradle) is the protected
 * brand artwork — rendered here from `/brand/nofee_logo_mark.png`, which
 * was extracted directly from the official artwork the brand owner
 * uploaded. No SVG redraw, no AI variation, no recreation.
 *
 * The WORDMARK ("NoFee" — "No" in white, "Fee" in gold) is reproduced
 * as live typography next to the mark because the source asset's white
 * "No" cannot be lifted off a white background without losing the letters.
 * Typography is not the protected piece — the mark is.
 *
 * One single source for every logo placement across the platform:
 *   - GoldBellMark    : square mark only (icon)
 *   - NofeeNavLogo    : compact lockup (mark + wordmark)
 *   - NofeeLogoFull   : large hero lockup + tagline
 *   - NoFeeLogo (def) : alias for GoldBellMark for legacy imports
 */

const OFFICIAL_MARK = '/brand/nofee_logo_mark.png';

export const GoldBellMark = ({ size = 64, className = '' }) => (
  <img
    src={OFFICIAL_MARK}
    alt="NoFee"
    width={size}
    height={size}
    className={className}
    draggable={false}
    style={{ objectFit: 'contain', display: 'inline-block' }}
    data-testid="nofee-bell-mark"
  />
);

export const NofeeNavLogo = ({ height = 40, className = '', variant = 'light' }) => (
  <div
    className={`flex items-center gap-2 ${className}`}
    style={{ height: `${height}px` }}
    data-testid="nofee-nav-logo"
  >
    <img
      src={OFFICIAL_MARK}
      alt="NoFee logo — 0% commission restaurant ordering platform"
      style={{ height: `${height}px`, width: `${height}px`, objectFit: 'contain' }}
      draggable={false}
    />
    <span
      className="font-black tracking-tight leading-none select-none"
      style={{ fontSize: `${height * 0.62}px` }}
    >
      <span className={variant === 'dark' ? 'text-zinc-900' : 'text-white'}>No</span>
      <span className="bg-gradient-to-b from-[#F8D67A] via-[#E0A830] to-[#A37518] bg-clip-text text-transparent">
        Fee
      </span>
    </span>
  </div>
);

/**
 * Full hero logo: large lockup + tagline below.
 */
export const NofeeLogoFull = ({ height = 140, className = '' }) => (
  <div className={`flex flex-col items-center ${className}`} data-testid="nofee-logo-full">
    <NofeeNavLogo height={height} />
    <div
      className="mt-3 sm:mt-4 font-bold tracking-[0.2em] flex items-center gap-2 text-center"
      style={{ fontSize: `${Math.max(11, height * 0.085)}px` }}
    >
      <span className="text-white">MORE ORDERS.</span>
      <span className="text-white">MORE PROFIT.</span>
      <span className="text-gold-500">NO FEES.</span>
    </div>
  </div>
);

const NoFeeLogo = ({ size = 36, className = '' }) => (
  <GoldBellMark size={size} className={className} />
);

export default NoFeeLogo;
